import { V2ATransfer, V2ATransferStatus } from '@varo/graphql-schema-types';
import { formatDate } from 'date-fns/format';
import { useRouter } from 'expo-router';
import useTransferHistory from 'features/v2a/sender/hooks/useTransferHistory';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { transferStatusTag } from 'shared/components/TransferHistory/transfer.helper';
import TransferHistoryWrapper from 'shared/components/TransferHistory/TransferHistoryWrapper';
import { TagProps } from 'shared/components/vxl/Tag/Tag';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import theme from 'shared/styles/theme';
import { toTitleCase } from 'shared/utils/string/string.helpers';

const TransferHistory = () => {
  const {
    data: transferList = [],
    error,
    loading,
    loadMore,
  } = useTransferHistory();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferHistory',
  });
  const router = useRouter();

  const titleTextString = (transfer: V2ATransfer) =>
    `${translatedText('from')}${transfer?.from?.full}`;

  const secondaryTextString = (transfer: V2ATransfer) =>
    `${formatDate(transfer.createdAt, 'MMM dd')} • ${translatedText('to')} ${transfer?.to?.displayName}`;

  const renderTags = (transfer: V2ATransfer): TagProps[] | undefined =>
    transfer?.status !== V2ATransferStatus.Completed
      ? [
          {
            text: toTitleCase(transfer.status),
            type: transferStatusTag(transfer.status),
          },
        ]
      : undefined;

  useViewDidFocusScreenAnalyticsEvent({
    key: 'V2ATransferHistory',
  });

  const v2ATransactionViewedEvent = usePressableAnalytics({
    analyticsId: 'v2ATransactionViewed',
  });

  const renderEmptyState = () => {
    return (
      <View style={styles.emptyWrapper}>
        <EmptyStateScreen
          bodyText={translatedText('v2aEmptyStateBodyText')}
          headlineText={translatedText('v2aEmptyStateHeadlineText')}
          illustrationKey="PLANE_B"
          analyticsId="v2aClicked"
          actionButtonText={translatedText('v2aEmptyStateButtonLabel')}
          actionOnPress={() => router.replace('/move-money/v2a')}
        />
      </View>
    );
  };

  return (
    <TransferHistoryWrapper<V2ATransfer>
      title={translatedText('v2aTitle')}
      transferList={transferList}
      error={!!error}
      loading={loading}
      loadMore={loadMore}
      onPressItem={(item) => {
        v2ATransactionViewedEvent();
        router.push({
          pathname: './transfer-details',
          params: { transferId: item.id },
        });
      }}
      renderTitle={titleTextString}
      renderSecondaryText={secondaryTextString}
      renderTags={renderTags}
      renderEmptyState={renderEmptyState}
    />
  );
};

const styles = StyleSheet.create({
  emptyWrapper: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default TransferHistory;
